import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import api from "../lib/api";

/**
 * Landing-page "Wear the Movement" strip.
 * Pulls the top 4 active merch items (sorted by sort_order) and shows them in a
 * horizontal grid so every landing visitor sees the shop without having to click into it.
 */
export default function FeaturedShop() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    api.get("/merchandise")
      .then((r) => { if (!cancelled) setItems(r.data.slice(0, 4)); })
      .catch(() => { if (!cancelled) setItems([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  if (loading || items.length === 0) return null;

  return (
    <section
      className="border-t border-bdu-gold/10 py-24"
      data-testid="landing-featured-shop"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-10">
        <div className="flex items-end justify-between gap-6 flex-wrap mb-12">
          <div>
            <div className="label-eyebrow">Fund Merchandise</div>
            <h2 className="mt-4 font-serif text-4xl sm:text-5xl text-bdu-cream leading-tight">
              Wear the movement.
              <br />
              <span className="italic text-bdu-gold">Fund the fund.</span>
            </h2>
            <p className="mt-4 text-sm text-bdu-cream/60 max-w-xl">
              Every tee, cap, and hoodie is a walking billboard for the Trillion Dollar Spending Power Fund.
              Every sale routes profit into the TDSP Fund's Citizen Trust Bank account.
            </p>
          </div>
          <Link
            to="/shop"
            className="text-xs uppercase tracking-widest text-bdu-gold hover:text-bdu-cream transition inline-flex items-center gap-2"
            data-testid="featured-shop-view-all"
          >
            Shop the drop <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-7">
          {items.map((m, idx) => (
            <Link
              key={m.id}
              to="/shop"
              className="group block border border-bdu-gold/15 hover:border-bdu-gold transition"
              data-testid={`featured-shop-item-${idx}`}
            >
              <div className="aspect-square bg-bdu-surface overflow-hidden">
                {m.image_url ? (
                  <img
                    src={m.image_url}
                    alt={m.name}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-bdu-cream/20 text-xs">
                    no image
                  </div>
                )}
              </div>
              <div className="p-4 sm:p-5">
                <div className="flex items-center gap-2">
                  <div className="text-[0.6rem] uppercase tracking-widest text-bdu-gold/70">
                    {m.category === "tshirt" ? "Apparel"
                      : m.category === "hat" ? "Headwear"
                      : m.category === "hoodie" || m.category === "other" ? "Fleece"
                      : m.category}
                  </div>
                  {m.brand === "rise_ii_power" && (
                    <span className="text-[0.55rem] uppercase tracking-widest px-1.5 py-0.5 border border-bdu-cream/30 text-bdu-cream/70">
                      Rise II Power
                    </span>
                  )}
                </div>
                <h3 className="mt-2 font-serif text-lg text-bdu-cream leading-snug line-clamp-2">
                  {m.name.replace("Black Dollars United — ", "").replace("Rise II Power — ", "")}
                </h3>
                <div className="mt-3 flex items-baseline justify-between">
                  <div className="text-bdu-gold font-serif text-xl">${(m.price_cents / 100).toFixed(0)}</div>
                  <div className="text-[0.65rem] uppercase tracking-widest text-bdu-cream/40 group-hover:text-bdu-gold transition">
                    Buy →
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
